import type { Character, Novel } from "./types.js";

/**
 * A character as it may appear on disk: the current shape plus the legacy
 * fields (`role`/`personality`/`motivation`/`visual_description`) that older
 * novels were generated with, before the Sims-style trait model.
 */
type StoredCharacter = Partial<Character> & {
  id: string;
  name: string;
  portrait_asset_id: string;
  role?: unknown;
  personality?: unknown;
  motivation?: unknown;
  visual_description?: unknown;
};

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Migrate one character to the current model: legacy `visual_description`
 * becomes `appearance`; `role`/`personality`/`motivation` are dropped; missing
 * `gender`/`age`/`traits` get empty defaults. Already-current characters pass
 * through unchanged in shape.
 */
export function normalizeCharacter(raw: StoredCharacter): Character {
  const character: Character = {
    id: raw.id,
    name: raw.name,
    gender: asString(raw.gender),
    age: typeof raw.age === "number" ? raw.age : 0,
    traits: Array.isArray(raw.traits) ? raw.traits.filter((t) => typeof t === "string") : [],
    appearance: raw.appearance !== undefined ? asString(raw.appearance) : asString(raw.visual_description),
    portrait_asset_id: raw.portrait_asset_id
  };
  if (raw.emotion_portraits) character.emotion_portraits = raw.emotion_portraits;
  return character;
}

/** True when any character still carries legacy fields / lacks the new ones. */
function needsMigration(novel: Novel): boolean {
  return novel.characters.some((character) => {
    const stored = character as StoredCharacter;
    return (
      stored.role !== undefined ||
      stored.personality !== undefined ||
      stored.motivation !== undefined ||
      stored.visual_description !== undefined ||
      typeof stored.gender !== "string" ||
      typeof stored.age !== "number" ||
      !Array.isArray(stored.traits) ||
      stored.appearance === undefined
    );
  });
}

/**
 * Return a novel whose characters are in the current model. A no-op (returns the
 * same reference) when every character is already current, so it's cheap to call
 * on every load.
 */
export function normalizeNovel(novel: Novel): Novel {
  if (!needsMigration(novel)) return novel;
  return {
    ...novel,
    characters: novel.characters.map((character) => normalizeCharacter(character as StoredCharacter))
  };
}

/**
 * Return a novel with the world-state system removed: every choice's `effects`
 * and `required_state`, every scene's `state_changes`, and the novel's
 * `state_labels`. The player then treats the story as pure branching — every
 * choice is always available and leads straight to its scene, with no gated
 * options and no "+trust / +respect" popups.
 *
 * Applied at the read boundary (the web client's novel load), NOT in storage or
 * generation, so it is fully reversible: drop this call to bring the state
 * system back. Always returns a new object; never mutates the input.
 */
export function stripStateSystem(novel: Novel): Novel {
  const stripped: Novel = {
    ...novel,
    scenes: novel.scenes.map((scene) => ({
      ...scene,
      choices: scene.choices.map((choice) => ({ ...choice }))
    }))
  };
  delete stripped.state_labels;
  for (const scene of stripped.scenes) {
    delete scene.state_changes;
    for (const choice of scene.choices) {
      delete choice.effects;
      delete choice.required_state;
    }
  }
  return stripped;
}
